/**!
 * Created by Leonardo Cheslacki on 23/02/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import HelpersController from './HelpersController';

import WithType from './WithType';

import Keyboard from 'simple-keyboard';

import layout from 'simple-keyboard-layouts/build/layouts/brazilian';

let _this = {};

/**
 *
 */
class HelpersKeyboard extends HelpersController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main) {
        super(props);
        this.props = props;
        this.main = main;

        this.state = {
            inputs: {}
        }

        this.$keyboard = {};

        this.$current_input = {};

        _this = this;
    };

    /**
     *
     * @returns {*}
     */
    get = () => {
        const {current_name} = this.props;

        return this.main.getState(`_this_${current_name}`);
    };

    handleShift = () => {
        const
            currentLayout = this.$keyboard.options.layoutName,
            shiftToggle = currentLayout === "default" ? "shift" : "default";

        this.$keyboard.setOptions({
            layoutName: shiftToggle
        });
    }

    /**
     *
     * @param value
     */
    onChange = (value) => {
        const {
            name,
            text_x,
            line_width,
            placeholder,
            reference: {occ_caret, occ_text, occ_placeholder}
        } = this.$current_input;

        this.$keyboard.setInput(value, name);

        let
            bounds_old = occ_text.getBounds(),
            width_old = (this.verifyField(bounds_old, 'width') ? bounds_old.width : 0);

        occ_text.text = value;

        occ_placeholder.text = (occ_text.text ? '' : placeholder);

        let
            bounds = occ_text.getBounds(),
            width_current = (this.verifyField(bounds, 'width') ? bounds.width : 0),
            width_character = (width_current - width_old),
            caret_x = 0;

        occ_text.lineWidth = (occ_text.lineWidth + width_character);

        if (width_current > line_width) {
            if (occ_text.textAlign === 'left') {
                caret_x = width_character;
                this.$current_input.switch_caret = 'story';
            }

            occ_text.textAlign = 'right';
            occ_text.x = (text_x + line_width);
        } else {
            caret_x = width_character;

            occ_text.textAlign = 'left';
            occ_text.x = text_x;

            if (this.$current_input.switch_caret === 'reset' && !this.isEmpty(this.$current_input.temp_caret_x)) {
                occ_caret.x = this.$current_input.temp_caret_x;
                this.$current_input.temp_caret_x = null;
                this.$current_input.switch_caret = 'default';
            }
        }

        if ((caret_x > 0) || (caret_x < 0)) {
            occ_caret.x = (occ_caret.x + caret_x);
        }

        if (this.$current_input.switch_caret === 'story' && this.isEmpty(this.$current_input.temp_caret_x)) {
            this.$current_input.temp_caret_x = occ_caret.x;
            occ_caret.x = occ_text.x;
            this.$current_input.switch_caret = 'reset';
        }
    }

    onKeyPress = (button) => {
        /**
         * If you want to handle the shift and caps lock buttons
         */
        if (
            button === "{shift}" ||
            button === "{shiftleft}" ||
            button === "{shiftright}" ||
            button === "{capslock}"
        )
            this.handleShift();
    }

    /**
     *
     * @param event
     * @param focus
     */
    onFocus = (event, focus = null) => {
        const
            {inputs} = this.state,
            current_name = ((this.isEmpty(event) && !this.isEmpty(focus)) ? focus : event.currentTarget.name.replace('input_text_', ''));

        this.$current_input = this.getState(`inputs.${current_name}`);

        this.$keyboard.setOptions({inputName: current_name});

        for (const property in inputs) {
            const {name, reference: {occ_caret}} = inputs[property];
            occ_caret.visible = !(current_name !== name);
        }
    };

    clearFields = () => {
        const
            {focus} = this.props,
            {inputs} = this.state;

        this.$keyboard.setOptions({inputName: focus});

        for (const property in inputs) {
            const {placeholder, reference: {occ_text, occ_placeholder, occ_caret}} = inputs[property];
            occ_text.text = '';
            occ_placeholder.text = placeholder;
            occ_caret.x = occ_text.x;
        }

        this.onFocus(null, focus);

        this.$keyboard.clearInput('name');
        this.$keyboard.clearInput('business');
        this.$keyboard.clearInput('email');
    };

    /**
     *
     * @param field
     * @returns {object|string}
     */
    getFields = (field = null) => {
        return !this.isEmpty(field) ? this.$keyboard.getInput(field) : this.$keyboard.getAllInputs();
    };

    /**
     *
     * @param errors
     */
    getErrors = (errors) => {
        const {inputs} = this.state;

        for (const property in errors) {
            const {reference: {occ_error}} = inputs[property];

            let error = this.getError(errors, property);

            occ_error.text = (error ? error : '');
        }
    };

    clearErrors = () => {
        const {inputs} = this.state;

        for (const property in inputs) {
            const {reference: {occ_error}} = inputs[property];
            occ_error.text = '';
        }
    };

    create = () => {
        const
            {text_font, text_font_size, text_y, names, focus} = this.props,
            {occ_form} = this.get();

        this.recursiveSyncMap(names, 0, names.length, (item, next) => {
            let input_text = occ_form[`input_text_${item}`];

            this.mountObject(`inputs.${item}`, {
                name: item,
                text_x: input_text.occ_text.x,
                line_width: input_text.occ_text.lineWidth,
                placeholder: input_text.occ_placeholder.text,
                reference: input_text,
                temp_caret_x: null,
                switch_caret: 'default'
            }, (object) => {
                this.setStates(object, () => {
                    input_text.occ_text.font = `${text_font_size}px '${text_font}'`;
                    input_text.occ_text.y = (input_text.occ_text.y + text_y);
                    input_text.occ_placeholder.font = `${text_font_size}px '${text_font}'`;
                    input_text.occ_placeholder.y = input_text.occ_text.y;
                    input_text.occ_placeholder.x = input_text.occ_text.x;
                    input_text.occ_error.font = `18px '${text_font}'`;
                    input_text.occ_error.y = (input_text.occ_error.y + 3);

                    this.removeListener('click', `click-input-text-${item}`, this.onFocus, () => {
                        this.addListener(input_text, 'click', `click-input-text-${item}`, this.onFocus);
                        next(true);
                    });
                });
            });
        }, () => {
            this.$current_input = this.getState(`inputs.${focus}`);

            const {reference: {occ_caret}} = this.$current_input;

            occ_caret.visible = true;

            this.$keyboard = (new Keyboard('.keyboard', {
                onChange: (input) => this.onChange(input),
                onKeyPress: (button) => this.onKeyPress(button),
                inputName: focus,
                theme: 'keyboard hg-theme-default hg-layout-default',
                physicalKeyboardHighlight: true,
                ...layout
            }));

            this.main.$jQuery('#_keyboard_').find('.keyboard').css('scale', (stage.scaleX + 0.3));
        });
    };
}

/**
 *
 * @type {{default_frame: Requireable<string>, text_font: Requireable<string>, text_font_size: Requireable<number>, names: Requireable<any[]>, name: Requireable<string>, focus: Requireable<string>, current_name: Requireable<string>, window: Requireable<object>, text_y: Requireable<number>}}
 */
HelpersKeyboard.propTypes = {
    name: PropTypes.string,
    default_frame: PropTypes.string,
    current_name: PropTypes.string,
    window: PropTypes.object,
    text_font: PropTypes.string,
    text_font_size: PropTypes.number,
    text_y: PropTypes.number,
    names: PropTypes.array,
    focus: PropTypes.string
};

/**
 *
 * @type {{default_frame: string, text_font: string, text_font_size: number, names: *[], name: string, focus: string, current_name: null, window: {}, text_y: number}}
 */
HelpersKeyboard.defaultProps = {
    name: 'keyboard',
    default_frame: 'frame01',
    current_name: null,
    window: {},
    text_font: 'MontserratMedium',
    text_font_size: 50,
    text_y: 12,
    names: [],
    focus: 'name'
};

export default WithType(HelpersKeyboard, {className: 'HelpersKeyboard'});